import React, { useEffect, useRef, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  query,
  setDoc,
  Timestamp,
  where,
} from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../services/firebase";
import { useAuth } from "../../providers/AuthProvider";
import { hasAnyGrant } from "../../services/shared";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Step,
  StepLabel,
  Stepper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ConfigLP } from "../../types";

interface OrgOption {
  uid: string;
  ragioneSociale: string;
  displayName: string;
}

interface UserOption {
  uid: string;
  displayName: string;
  email: string;
}

const TIPOLOGIA_OPTIONS = ["Web", "Telefono", "Referral", "Agenzia"];
const CANALE_OPTIONS = ["Organico", "Paid", "Referral", "Direct"];
const STEPS = ["Anagrafica", "Configurazione", "Integrazione"];

function generateShortLink(): string {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  return Array.from(
    { length: 6 },
    () => chars[Math.floor(Math.random() * chars.length)]
  ).join("");
}

export default function LpAgentCreate() {
  const navigate = useNavigate();
  const { grants, user } = useAuth();
  const canSeeAll = hasAnyGrant(grants, ["admin", "lp_admin"]);

  const [activeStep, setActiveStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 1
  const [title, setTitle] = useState("");
  const [orgsOptions, setOrgsOptions] = useState<OrgOption[]>([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<OrgOption | null>(null);
  const [usersOptions, setUsersOptions] = useState<UserOption[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserOption | null>(null);

  // Step 2
  const [campagnaID, setCampagnaID] = useState("");
  const [fornitore, setFornitore] = useState("");
  const [tipologiaLead, setTipologiaLead] = useState("");
  const [canale, setCanale] = useState("");
  const [segnalatoreID, setSegnalatoreID] = useState("");

  // Step 3 — shortlink auto-generato, stabile per tutta la sessione
  const [shortLink] = useState<string>(generateShortLink);
  const [photoMode, setPhotoMode] = useState<"url" | "file">("url");
  const [photoUrl, setPhotoUrl] = useState("");
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Carica organizzazioni
  useEffect(() => {
    setOrgsLoading(true);
    getDocs(collection(db, "organizations"))
      .then((snap) => {
        const list: OrgOption[] = snap.docs.map((d) => ({
          uid: d.id,
          ragioneSociale: d.data().ragioneSociale ?? "",
          displayName: d.data().displayName ?? "",
        }));
        setOrgsOptions(list);
        // Per lp_builder: pre-seleziona l'org dell'utente corrente
        if (!canSeeAll && user?.org) {
          const match = list.find((o) => o.uid === user.org);
          if (match) setSelectedOrg(match);
        }
      })
      .catch((err) => console.error(err))
      .finally(() => setOrgsLoading(false));
  }, [canSeeAll, user?.org]);

  // Carica utenti filtrati per org selezionata
  useEffect(() => {
    if (!selectedOrg) {
      setUsersOptions([]);
      setSelectedUser(null);
      return;
    }
    setUsersLoading(true);
    getDocs(
      query(collection(db, "users"), where("org", "==", selectedOrg.uid))
    )
      .then((snap) => {
        setUsersOptions(
          snap.docs.map((d) => ({
            uid: d.id,
            displayName: d.data().displayName ?? "",
            email: d.data().email ?? "",
          }))
        );
      })
      .catch((err) => console.error(err))
      .finally(() => setUsersLoading(false));
  }, [selectedOrg]);

  const validateStep = (): boolean => {
    if (activeStep === 0) {
      if (!title.trim()) {
        setError("Inserisci il titolo");
        return false;
      }
      if (!selectedOrg) {
        setError("Seleziona l'organizzazione");
        return false;
      }
    }
    if (activeStep === 1) {
      if (!campagnaID || isNaN(Number(campagnaID))) {
        setError("ID Campagna deve essere un numero");
        return false;
      }
      if (!fornitore.trim()) {
        setError("Inserisci il fornitore");
        return false;
      }
      if (!tipologiaLead) {
        setError("Seleziona la tipologia lead");
        return false;
      }
      if (!canale) {
        setError("Seleziona il canale");
        return false;
      }
    }
    setError(null);
    return true;
  };

  const handleNext = () => {
    if (validateStep()) setActiveStep((s) => s + 1);
  };

  const handleSave = async () => {
    if (!validateStep()) return;
    setSaving(true);
    setError(null);
    try {
      const newDocRef = doc(collection(db, "lp"));
      const hash = newDocRef.id;

      // Risolve URL foto: upload file se in modalità file, altrimenti usa URL diretta
      let resolvedPhotoUrl: string | undefined;
      if (photoMode === "file" && photoFile) {
        const path = selectedUser
          ? `agent_photos/${selectedUser.uid}/${Date.now()}_${photoFile.name}`
          : `agent_photos/_unassigned/${hash}/${Date.now()}_${photoFile.name}`;
        const fileRef = storageRef(storage, path);
        await uploadBytes(fileRef, photoFile);
        resolvedPhotoUrl = await getDownloadURL(fileRef);
      } else if (photoMode === "url" && photoUrl.trim()) {
        resolvedPhotoUrl = photoUrl.trim();
      }

      const config: ConfigLP = {
        campagnaID: Number(campagnaID),
        c__fowc2kq5: fornitore,
        c__BYBIDFRZ: tipologiaLead,
        c__hyez2gj1: canale,
        ...(segnalatoreID && { c__3j8d94nv: Number(segnalatoreID) }),
      };

      await setDoc(newDocRef, {
        type: "agent",
        title: title.trim(),
        org: selectedOrg!.uid,
        hash,
        createdAt: Timestamp.now(),
        config: JSON.stringify(config),
        shortLink,
        ...(selectedUser && { userRef: selectedUser.uid, displayNameAgente: selectedUser.displayName }),
        ...(resolvedPhotoUrl && { user_photo: resolvedPhotoUrl }),
      });

      navigate(`/lp-agents/${hash}`);
    } catch (err) {
      console.error(err);
      setError("Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container">
      <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
        Nuova Landing Page Agente
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* ── Step 1: Anagrafica ── */}
      {activeStep === 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Titolo landing page"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            fullWidth
          />
          <Autocomplete
            options={orgsOptions}
            loading={orgsLoading}
            disabled={!canSeeAll}
            getOptionLabel={(o) => o.ragioneSociale || o.displayName}
            value={selectedOrg}
            onChange={(_, val) => {
              setSelectedOrg(val);
              setSelectedUser(null);
            }}
            renderInput={(params) => (
              <TextField {...params} label="Organizzazione" required fullWidth />
            )}
          />
          <Autocomplete
            options={usersOptions}
            loading={usersLoading}
            disabled={!selectedOrg}
            getOptionLabel={(o) =>
              o.displayName ? `${o.displayName} (${o.email})` : o.email
            }
            value={selectedUser}
            onChange={(_, val) => setSelectedUser(val)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Utente agente"
                fullWidth
                helperText={!selectedOrg ? "Seleziona prima un'organizzazione" : ""}
              />
            )}
          />
        </Box>
      )}

      {/* ── Step 2: Configurazione ── */}
      {activeStep === 1 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="ID Campagna"
            type="number"
            value={campagnaID}
            onChange={(e) => setCampagnaID(e.target.value)}
            required
            fullWidth
          />
          <TextField
            label="Fornitore"
            value={fornitore}
            onChange={(e) => setFornitore(e.target.value)}
            required
            fullWidth
          />
          <TextField
            select
            label="Tipologia Lead"
            value={tipologiaLead}
            onChange={(e) => setTipologiaLead(e.target.value)}
            required
            fullWidth
          >
            {TIPOLOGIA_OPTIONS.map((o) => (
              <MenuItem key={o} value={o}>
                {o}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Canale"
            value={canale}
            onChange={(e) => setCanale(e.target.value)}
            required
            fullWidth
          >
            {CANALE_OPTIONS.map((o) => (
              <MenuItem key={o} value={o}>
                {o}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Segnalatore ID"
            type="number"
            value={segnalatoreID}
            onChange={(e) => setSegnalatoreID(e.target.value)}
            fullWidth
          />
        </Box>
      )}

      {/* ── Step 3: Integrazione ── */}
      {activeStep === 2 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="ShortLink"
            value={shortLink}
            disabled
            fullWidth
            helperText={`URL pubblica: https://preq.affida.credit/s2/${shortLink}`}
          />

          <Typography variant="body2" fontWeight={500} sx={{ mt: 1 }}>
            Foto agente
          </Typography>
          <ToggleButtonGroup
            value={photoMode}
            exclusive
            onChange={(_, v) => v && setPhotoMode(v)}
            size="small"
          >
            <ToggleButton value="url">Inserisci URL</ToggleButton>
            <ToggleButton value="file">Carica file</ToggleButton>
          </ToggleButtonGroup>

          {photoMode === "url" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <TextField
                label="URL foto agente"
                value={photoUrl}
                onChange={(e) => setPhotoUrl(e.target.value)}
                fullWidth
                helperText="URL immagine pubblica usata nella landing page"
              />
              {photoUrl && (
                <Box
                  component="img"
                  src={photoUrl}
                  alt="Anteprima"
                  sx={{
                    maxHeight: 120,
                    maxWidth: 200,
                    borderRadius: 1,
                    objectFit: "cover",
                    border: "1px solid #e0e0e0",
                  }}
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display = "none";
                  }}
                />
              )}
            </Box>
          )}

          {photoMode === "file" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => {
                  const f = e.target.files?.[0] ?? null;
                  setPhotoFile(f);
                }}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={() => fileInputRef.current?.click()}
                sx={{ alignSelf: "flex-start" }}
              >
                {photoFile ? "Cambia immagine" : "Seleziona immagine"}
              </Button>
              {photoFile && (
                <>
                  <Typography variant="caption" color="text.secondary">
                    {photoFile.name}
                  </Typography>
                  <Box
                    component="img"
                    src={URL.createObjectURL(photoFile)}
                    alt="Anteprima"
                    sx={{
                      maxHeight: 120,
                      maxWidth: 200,
                      borderRadius: 1,
                      objectFit: "cover",
                      border: "1px solid #e0e0e0",
                    }}
                  />
                </>
              )}
              {!selectedUser && (
                <Typography variant="caption" color="text.secondary">
                  Il file verrà salvato in un percorso temporaneo. Per associarlo
                  a un agente, seleziona l'utente nello Step 1.
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}

      <Box sx={{ display: "flex", gap: 1, mt: 3 }}>
        {activeStep > 0 && (
          <Button onClick={() => setActiveStep((s) => s - 1)} disabled={saving}>
            Indietro
          </Button>
        )}
        {activeStep < STEPS.length - 1 && (
          <Button variant="contained" onClick={handleNext}>
            Avanti
          </Button>
        )}
        {activeStep === STEPS.length - 1 && (
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} color="inherit" /> : "Salva"}
          </Button>
        )}
        <Button onClick={() => navigate("/lp-agents")} disabled={saving}>
          Annulla
        </Button>
      </Box>
    </div>
  );
}
